import { createTableAIProfile, copyTableAIProfile } from '@core/table-ai';
import {
  GameSetCompletionReason,
  GameSetStatus,
  RoundIndex,
  type IGameSetSession,
  type IRoundPlan,
} from '@core/game-set';
import {
  openingStackIndex,
  seatWindsFromOpening,
  selectOpeningSeat,
  wallStartStackIndex,
  type TDiceValues,
} from '@core/dice-rules';
import { Seat, Wind, type TSeat, type TWind } from '@core/types';

export const GAME_SAVE_KEY = 'mahjongking.save.v1';
const GAME_SAVE_SCHEMA_VERSION = 1;
const INITIAL_BALANCE = 1_000_000;

export const LedgerPlayerId = {
  Self: 'player-self',
  Right: 'npc-right',
  Opposite: 'npc-opposite',
  Left: 'npc-left',
} as const;

export type TLedgerPlayerId = (typeof LedgerPlayerId)[keyof typeof LedgerPlayerId];

export interface IKeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface IGameLedger {
  readonly playerIds: readonly [TLedgerPlayerId, TLedgerPlayerId, TLedgerPlayerId, TLedgerPlayerId];
  readonly balances: readonly [number, number, number, number];
  readonly appliedTransactionIds: readonly string[];
}

export interface IGameSave {
  readonly schemaVersion: number;
  readonly ledger: IGameLedger;
  readonly activeSession: IGameSetSession | null;
}

export interface IGameSaveLoadResult {
  readonly save: IGameSave;
  readonly corruptSessionDiscarded: boolean;
}

export interface IApplySettlementInput {
  readonly transactionId: string;
  readonly deltaBySeat: readonly [number, number, number, number];
  readonly activeSession: IGameSetSession | null;
}

export interface IApplySettlementResult {
  readonly save: IGameSave;
  readonly applied: boolean;
}

export class CGameSaveDataError extends Error {}
export class CGameSaveWriteError extends Error {}

const browserStorage = (): IKeyValueStorage | null =>
  typeof localStorage === 'undefined' ? null : localStorage;

export const createDefaultGameSave = (): IGameSave => ({
  schemaVersion: GAME_SAVE_SCHEMA_VERSION,
  ledger: {
    playerIds: [
      LedgerPlayerId.Self,
      LedgerPlayerId.Right,
      LedgerPlayerId.Opposite,
      LedgerPlayerId.Left,
    ],
    balances: [INITIAL_BALANCE, INITIAL_BALANCE, INITIAL_BALANCE, INITIAL_BALANCE],
    appliedTransactionIds: [],
  },
  activeSession: null,
});

const isFiniteInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value);

const isSeat = (value: unknown): value is TSeat =>
  value === Seat.Self || value === Seat.Right || value === Seat.Opposite || value === Seat.Left;

const isWind = (value: unknown): value is TWind => Object.values(Wind).includes(value as never);

const isDice = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length === 3 &&
  value.every((die) => isFiniteInteger(die) && die >= 1 && die <= 6);

const isRoundPlan = (value: unknown): value is IRoundPlan => {
  if (typeof value !== 'object' || value === null) return false;
  const plan = value as Partial<IRoundPlan>;
  const structurallyValid =
    typeof plan.roundId === 'string' &&
    typeof plan.transactionId === 'string' &&
    isSeat(plan.dealerSeat) &&
    isFiniteInteger(plan.dealerStreak) &&
    plan.dealerStreak >= 0 &&
    isWind(plan.prevailingWind) &&
    Object.values(RoundIndex).includes(plan.roundIndex as never) &&
    isSeat(plan.openingSeat) &&
    isFiniteInteger(plan.openingStackIndex) &&
    plan.openingStackIndex >= 0 &&
    plan.openingStackIndex < 18 &&
    isFiniteInteger(plan.wallStartStackIndex) &&
    plan.wallStartStackIndex >= 0 &&
    plan.wallStartStackIndex < 72 &&
    Array.isArray(plan.seatWinds) &&
    plan.seatWinds.length === 4 &&
    plan.seatWinds.every(isWind) &&
    (plan.dealerDice === null || isDice(plan.dealerDice)) &&
    isDice(plan.openingDice) &&
    isFiniteInteger(plan.wallSeed) &&
    isFiniteInteger(plan.base) &&
    plan.base > 0 &&
    isFiniteInteger(plan.taiUnit) &&
    plan.taiUnit > 0 &&
    typeof plan.settlementApplied === 'boolean';
  if (!structurallyValid) return false;

  const dealerSeat = plan.dealerSeat as TSeat;
  const openingSeat = plan.openingSeat as TSeat;
  const openingDice = plan.openingDice as TDiceValues;
  const expectedWinds = seatWindsFromOpening(openingSeat);
  return (
    openingSeat === selectOpeningSeat(dealerSeat, openingDice) &&
    plan.openingStackIndex === openingStackIndex(openingDice) &&
    plan.wallStartStackIndex === wallStartStackIndex(openingSeat, openingDice) &&
    plan.seatWinds?.every((wind, seat) => wind === expectedWinds[seat])
  );
};

const isLedger = (value: unknown): value is IGameLedger => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<IGameLedger>;
  return (
    Array.isArray(candidate.playerIds) &&
    candidate.playerIds.length === 4 &&
    candidate.playerIds[0] === LedgerPlayerId.Self &&
    candidate.playerIds[1] === LedgerPlayerId.Right &&
    candidate.playerIds[2] === LedgerPlayerId.Opposite &&
    candidate.playerIds[3] === LedgerPlayerId.Left &&
    Array.isArray(candidate.balances) &&
    candidate.balances.length === 4 &&
    candidate.balances.every(isFiniteInteger) &&
    Array.isArray(candidate.appliedTransactionIds) &&
    candidate.appliedTransactionIds.every((id) => typeof id === 'string')
  );
};

const isActiveSession = (value: unknown): value is IGameSetSession => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<IGameSetSession>;
  const statusValid = Object.values(GameSetStatus).includes(candidate.status as never);
  const completionReasonValid =
    candidate.completionReason === null ||
    Object.values(GameSetCompletionReason).includes(candidate.completionReason as never);
  const requiresRound = candidate.status === GameSetStatus.RoundInProgress;
  const statusAndCompletionAgree =
    candidate.status === GameSetStatus.Completed
      ? candidate.completionReason !== null
      : candidate.completionReason === null;
  const structurallyValid =
    candidate.schemaVersion === GAME_SAVE_SCHEMA_VERSION &&
    typeof candidate.sessionId === 'string' &&
    statusValid &&
    completionReasonValid &&
    statusAndCompletionAgree &&
    Array.isArray(candidate.players) &&
    candidate.players.length === 4 &&
    candidate.players.every(
      (player, seat) =>
        typeof player === 'object' &&
        player !== null &&
        typeof player.id === 'string' &&
        typeof player.name === 'string' &&
        player.seat === seat
    ) &&
    isSeat(candidate.playerSeat) &&
    isSeat(candidate.fakeEastSeat) &&
    isSeat(candidate.initialDealerSeat) &&
    isSeat(candidate.dealerSeat) &&
    isWind(candidate.prevailingWind) &&
    Object.values(RoundIndex).includes(candidate.roundIndex as never) &&
    isFiniteInteger(candidate.dealerStreak) &&
    candidate.dealerStreak >= 0 &&
    isFiniteInteger(candidate.completedRounds) &&
    candidate.completedRounds >= 0 &&
    isFiniteInteger(candidate.completedCircles) &&
    candidate.completedCircles >= 0 &&
    Array.isArray(candidate.startingBalances) &&
    candidate.startingBalances.length === 4 &&
    candidate.startingBalances.every(isFiniteInteger) &&
    typeof candidate.bet === 'object' &&
    candidate.bet !== null &&
    isFiniteInteger(candidate.bet.base) &&
    candidate.bet.base > 0 &&
    isFiniteInteger(candidate.bet.taiUnit) &&
    candidate.bet.taiUnit > 0 &&
    (requiresRound ? isRoundPlan(candidate.currentRound) : candidate.currentRound === null);
  if (!structurallyValid) return false;
  if (!requiresRound) return true;

  const plan = candidate.currentRound as IRoundPlan;
  return (
    plan.dealerSeat === candidate.dealerSeat &&
    plan.dealerStreak === candidate.dealerStreak &&
    plan.prevailingWind === candidate.prevailingWind &&
    plan.roundIndex === candidate.roundIndex &&
    plan.base === candidate.bet?.base &&
    plan.taiUnit === candidate.bet?.taiUnit &&
    plan.settlementApplied === false &&
    (candidate.completedRounds === 0 ? plan.dealerDice !== null : plan.dealerDice === null)
  );
};

const persist = (storage: IKeyValueStorage, save: IGameSave): void => {
  try {
    storage.setItem(GAME_SAVE_KEY, JSON.stringify(save));
  } catch (error) {
    throw new CGameSaveWriteError(
      `Unable to persist MahjongKing save: ${error instanceof Error ? error.message : String(error)}`
    );
  }
};

export const loadGameSaveResult = (
  storage: IKeyValueStorage | null = browserStorage()
): IGameSaveLoadResult => {
  if (!storage) return { save: createDefaultGameSave(), corruptSessionDiscarded: false };
  const raw = storage.getItem(GAME_SAVE_KEY);
  if (raw === null) {
    const save = createDefaultGameSave();
    persist(storage, save);
    return { save, corruptSessionDiscarded: false };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new CGameSaveDataError('Permanent MahjongKing save is not valid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new CGameSaveDataError('Permanent MahjongKing save root is invalid');
  }
  const candidate = parsed as Partial<IGameSave>;
  if (candidate.schemaVersion !== GAME_SAVE_SCHEMA_VERSION || !isLedger(candidate.ledger)) {
    throw new CGameSaveDataError('Permanent MahjongKing ledger is invalid');
  }

  if (candidate.activeSession === null || candidate.activeSession === undefined) {
    return {
      save: {
        schemaVersion: GAME_SAVE_SCHEMA_VERSION,
        ledger: candidate.ledger,
        activeSession: null,
      },
      corruptSessionDiscarded: false,
    };
  }
  if (!isActiveSession(candidate.activeSession)) {
    return {
      save: {
        schemaVersion: GAME_SAVE_SCHEMA_VERSION,
        ledger: candidate.ledger,
        activeSession: null,
      },
      corruptSessionDiscarded: true,
    };
  }
  // 舊版只補相容的固定配置，保持 ledger 與 currentRound 原樣。版本錯誤不丟棄存檔。
  const missingAI = candidate.activeSession.tableAI === undefined;
  const tableAI = missingAI
    ? createTableAIProfile()
    : copyTableAIProfile(candidate.activeSession.tableAI);
  const save: IGameSave = {
    schemaVersion: GAME_SAVE_SCHEMA_VERSION,
    ledger: candidate.ledger,
    activeSession: { ...candidate.activeSession, tableAI },
  };
  if (missingAI) persist(storage, save);
  return { save, corruptSessionDiscarded: false };
};

export const loadGameSave = (storage: IKeyValueStorage | null = browserStorage()): IGameSave =>
  loadGameSaveResult(storage).save;

export const saveGameSave = (
  save: IGameSave,
  storage: IKeyValueStorage | null = browserStorage()
): void => {
  if (!storage) return;
  if (save.schemaVersion !== GAME_SAVE_SCHEMA_VERSION || !isLedger(save.ledger)) {
    throw new CGameSaveDataError('Refusing to write invalid MahjongKing save');
  }
  if (save.activeSession !== null && !isActiveSession(save.activeSession)) {
    throw new CGameSaveDataError('Refusing to write invalid MahjongKing active session');
  }
  if (save.activeSession?.tableAI !== undefined) copyTableAIProfile(save.activeSession.tableAI);
  persist(storage, save);
};

export const applySettlementTransaction = (
  input: IApplySettlementInput,
  storage: IKeyValueStorage | null = browserStorage()
): IApplySettlementResult => {
  const current = loadGameSave(storage);
  if (current.ledger.appliedTransactionIds.includes(input.transactionId)) {
    return { save: current, applied: false };
  }

  const balances = current.ledger.balances;
  const next: IGameSave = {
    schemaVersion: GAME_SAVE_SCHEMA_VERSION,
    ledger: {
      ...current.ledger,
      balances: [
        balances[0] + input.deltaBySeat[0],
        balances[1] + input.deltaBySeat[1],
        balances[2] + input.deltaBySeat[2],
        balances[3] + input.deltaBySeat[3],
      ],
      appliedTransactionIds: [...current.ledger.appliedTransactionIds, input.transactionId],
    },
    activeSession: input.activeSession,
  };
  saveGameSave(next, storage);
  return { save: next, applied: true };
};

export const abandonActiveSession = (
  storage: IKeyValueStorage | null = browserStorage()
): IGameSave => {
  const current = loadGameSave(storage);
  const next = { ...current, activeSession: null };
  saveGameSave(next, storage);
  return next;
};

export const saveActiveSession = (
  activeSession: IGameSetSession,
  storage: IKeyValueStorage | null = browserStorage()
): IGameSave => {
  const current = loadGameSave(storage);
  const next = { ...current, activeSession };
  saveGameSave(next, storage);
  return next;
};
